// The fake api's online payments, past the console gate in `fake-server.ts` (spec/06 §9.3, F-PAY-03,
// F-WAL-02) — enough for Sell's Online (UPI) tender and the wallet's online top-up to run in a browser
// test against the contract's own shapes.
//
//   GET  …/venues/{v}/payments/online                         getOnlinePaymentsStatus   orders:read
//   POST …/venues/{v}/sales/{id}/payment-intents              createSalePaymentIntent   orders:create
//   GET  …/venues/{v}/payment-intents/{id}                    getPaymentIntent          orders:read
//   POST …/venues/{v}/payment-intents/{id}/cancel             cancelPaymentIntent       orders:create
//   POST /v1/organizations/{o}/players/{p}/wallet/online-top-ups   beginOnlineWalletTopUp  wallet:topup
//
// It is the server's side and the provider's at once. `state.onlineEnabled` is the venue's gate
// (`payments.onlineEnabled`, off by default as on the server). A test plays the player through
// `capture`, `fail` and `expire`: a capture records the sale's `online` payment through the commerce
// fake, or credits the wallet through the players fake — exactly once, as the webhook handler does.
// Bodies are checked with the contract's own schemas; money is integer minor units of INR.

import type { Money, OnlinePaymentsStatus, PaymentIntent, Problem } from '@clavian/api-client';
import { CreateSalePaymentIntentSchema, WalletOnlineTopUpRequestSchema, type z } from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

// Re-declared rather than imported, so this file and `fake-server.ts` do not import each other.
const ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';
const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
const ORGANIZATION_PATH = `/v1/organizations/${ORGANIZATION_ID}`;
const VENUE_PATH = `${ORGANIZATION_PATH}/venues/${VENUE_ID}`;
/** The fixtures' "now". */
const NOW = '2026-09-26T14:00:00.000Z';
/** `payments.intentTtlMinutes`' default. */
const TTL_MINUTES = 15;

export interface PaymentsHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  readonly gate: () => FakeResponse | undefined;
  readonly can?: (permission: string) => boolean;
  /** The commerce fake: what a sale still owes, and recording a captured online payment against it. */
  readonly sales?: {
    readonly outstandingOf: (saleId: string) => Money | undefined;
    readonly recordOnlinePayment: (saleId: string, amount: Money, providerPaymentId: string) => void;
  };
  /** The players fake: credit a wallet with a captured online top-up. */
  readonly wallets?: { readonly creditOnline: (playerId: string, amount: number) => void };
}

/** The paths this file answers, for a caller that routes by pattern. */
export const PAYMENTS_PATHS: readonly RegExp[] = [
  new RegExp(`^${VENUE_PATH}/payments/online$`),
  new RegExp(`^${VENUE_PATH}/sales/[^/]+/payment-intents$`),
  new RegExp(`^${VENUE_PATH}/payment-intents/[^/]+(?:/cancel)?$`),
  new RegExp(`^${ORGANIZATION_PATH}/players/[^/]+/wallet/online-top-ups$`),
];

export function createFakePayments(helpers: PaymentsHelpers) {
  const { json, problem, gate } = helpers;
  const can = helpers.can ?? (() => true);

  const state = {
    /** The venue's `payments.onlineEnabled`. Off, as on the server, until a test turns it on. */
    onlineEnabled: false,
    /** The deployment's provider; null answers as a platform with none. */
    provider: 'fake' as string | null,
    /** The organisation's payee at the provider (null: it settles to the platform, no payee). */
    payeeStatus: null as OnlinePaymentsStatus['payeeStatus'],
    intents: [] as PaymentIntent[],
    serial: 0,
  };

  const invalid = (error: z.ZodError) =>
    problem(400, 'validation_failed', 'Validation failed', {
      errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
    });
  const forbidden = () => problem(403, 'forbidden', 'Not allowed');
  const methodNotAllowed = () => problem(405, 'method_not_allowed', 'Method not allowed');
  const closed = () => problem(409, 'online_payments_unavailable', 'Online payments are not available here');

  function status(): OnlinePaymentsStatus {
    const pending = state.payeeStatus !== null && state.payeeStatus !== 'active';
    return {
      enabled: state.onlineEnabled && !pending,
      gate: !state.onlineEnabled ? 'disabled' : pending ? 'payee_not_active' : 'open',
      provider: state.provider,
      payeeStatus: state.payeeStatus,
    };
  }

  function open(input: { purpose: PaymentIntent['purpose']; saleId: string | null; playerId: string | null; amount: Money }): PaymentIntent {
    state.serial += 1;
    const serial = String(state.serial).padStart(5, '0');
    const id = `pit_01k5znw4hmfay8vvr14d2${serial}`;
    const providerIntent = `fake_int_${serial}`;
    const cashfree = state.provider === 'cashfree';
    const intent: PaymentIntent = {
      id,
      purpose: input.purpose,
      saleId: input.saleId,
      playerId: input.playerId,
      status: 'pending',
      amount: input.amount,
      capturedAmount: null,
      method: null,
      provider: cashfree ? 'cashfree' : 'fake',
      // Cashfree has no UPI link: the QR is the platform's checkout page, opened with the phone's camera.
      upiIntentUrl: cashfree ? '' : `upi://pay?pa=fake@clavian&am=${(input.amount.amount / 100).toFixed(2)}&tr=${providerIntent}`,
      qrPayload: cashfree ? 'https://api.clavian.test/v1/payments/checkout/cashfree#session_abc123' : `fake-qr:${providerIntent}`,
      checkoutUrl: `https://fake-provider.local/checkout/${providerIntent}`,
      shortLink: `https://fake-provider.local/l/${providerIntent}`,
      expiresAt: new Date(Date.parse(NOW) + TTL_MINUTES * 60_000).toISOString(),
      failureReason: null,
      createdAt: NOW,
      capturedAt: null,
    };
    state.intents.push(intent);
    return intent;
  }

  function replace(next: PaymentIntent): PaymentIntent {
    state.intents = state.intents.map((one) => (one.id === next.id ? next : one));
    return next;
  }

  const find = (intentId: string) => state.intents.find((one) => one.id === intentId);

  // -- the player and the provider, for a test -------------------------------------------------

  /** The player paid: the intent captures, and the sale or the wallet is recorded once. */
  function capture(intentId: string): PaymentIntent {
    const intent = find(intentId);
    if (intent === undefined || intent.status !== 'pending') throw new Error(`No pending intent ${intentId} to capture.`);
    const paymentId = `fake_pay_${intent.id.slice(-5)}`;
    if (intent.purpose === 'sale' && intent.saleId !== null) helpers.sales?.recordOnlinePayment(intent.saleId, intent.amount, paymentId);
    if (intent.purpose === 'wallet_topup' && intent.playerId !== null) helpers.wallets?.creditOnline(intent.playerId, intent.amount.amount);
    return replace({ ...intent, status: 'succeeded', capturedAmount: intent.amount, method: 'upi', capturedAt: NOW });
  }

  /** The player's payment failed at the provider. */
  function fail(intentId: string, reason: string): PaymentIntent {
    const intent = find(intentId);
    if (intent === undefined || intent.status !== 'pending') throw new Error(`No pending intent ${intentId} to fail.`);
    return replace({ ...intent, status: 'failed', failureReason: reason });
  }

  /** The intent ran out of time unpaid. */
  function expire(intentId: string): PaymentIntent {
    const intent = find(intentId);
    if (intent === undefined || intent.status !== 'pending') throw new Error(`No pending intent ${intentId} to expire.`);
    return replace({ ...intent, status: 'expired' });
  }

  /** `undefined` for a path that is not online payments'. */
  function handle(method: string, pathname: string, _query: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    if (!PAYMENTS_PATHS.some((pattern) => pattern.test(pathname))) return undefined;
    const refused = gate();
    if (refused !== undefined) return refused;

    if (pathname === `${VENUE_PATH}/payments/online`) {
      if (method !== 'GET') return methodNotAllowed();
      if (!can('orders:read')) return forbidden();
      return json(200, status());
    }

    const forSale = new RegExp(`^${VENUE_PATH}/sales/([^/]+)/payment-intents$`).exec(pathname);
    if (forSale !== null) {
      if (method !== 'POST') return methodNotAllowed();
      if (!can('orders:create')) return forbidden();
      const parsed = CreateSalePaymentIntentSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      if (!state.onlineEnabled) return closed();
      const saleId = forSale[1] ?? '';
      const outstanding = helpers.sales?.outstandingOf(saleId);
      if (outstanding === undefined) return problem(409, 'conflict', 'The sale is cancelled, already paid, or not here.');
      const amount = parsed.data.amount ?? outstanding;
      if (amount.currency !== outstanding.currency || amount.amount < 100 || amount.amount > outstanding.amount) {
        return problem(409, 'conflict', 'The amount must be at least ₹1 and at most what is outstanding.');
      }
      // One open intent per sale (spec/06 §9.3): a new one cancels the previous.
      for (const earlier of state.intents.filter((one) => one.saleId === saleId && one.status === 'pending')) replace({ ...earlier, status: 'cancelled' });
      return json(201, open({ purpose: 'sale', saleId, playerId: null, amount }));
    }

    const onIntent = new RegExp(`^${VENUE_PATH}/payment-intents/([^/]+)(/cancel)?$`).exec(pathname);
    if (onIntent !== null) {
      const intent = find(onIntent[1] ?? '');
      if (onIntent[2] === undefined) {
        if (method !== 'GET') return methodNotAllowed();
        if (!can('orders:read')) return forbidden();
        return intent === undefined ? problem(404, 'not_found', 'No such payment intent at this venue.') : json(200, intent);
      }
      if (method !== 'POST') return methodNotAllowed();
      if (!can('orders:create')) return forbidden();
      if (intent === undefined) return problem(404, 'not_found', 'No such payment intent at this venue.');
      if (intent.status === 'succeeded') return problem(409, 'conflict', 'The provider has captured this payment.');
      return json(200, intent.status === 'pending' ? replace({ ...intent, status: 'cancelled' }) : intent);
    }

    const topUp = new RegExp(`^${ORGANIZATION_PATH}/players/([^/]+)/wallet/online-top-ups$`).exec(pathname);
    if (topUp !== null) {
      if (method !== 'POST') return methodNotAllowed();
      if (!can('wallet:topup')) return forbidden();
      const parsed = WalletOnlineTopUpRequestSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      if (!state.onlineEnabled) return closed();
      if (parsed.data.amount.currency !== 'INR' || parsed.data.amount.amount < 100) {
        return problem(409, 'wallet_limit', 'The amount is outside the wallet’s limits.');
      }
      return json(201, open({ purpose: 'wallet_topup', saleId: null, playerId: topUp[1] ?? null, amount: parsed.data.amount }));
    }

    return undefined;
  }

  return { state, handle, capture, fail, expire };
}

export type FakePayments = ReturnType<typeof createFakePayments>;
